import React, { createContext, useContext, useState, useEffect } from 'react';

const BrandContext = createContext();

export const useBrands = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrands must be used within a BrandProvider');
  }
  return context;
};

export const BrandProvider = ({ children }) => {
  const [brands, setBrands] = useState([]);
  const [showToast, setShowToast] = useState({ show: false, message: '', type: '' });

  // Load brands from localStorage on mount
  useEffect(() => {
    const savedBrands = localStorage.getItem('brands');
    if (savedBrands) {
      const parsedBrands = JSON.parse(savedBrands);
      setBrands(parsedBrands);
    } else {
      // Default brands
      const defaultBrands = [
        {
          id: 1,
          name: 'MINIMAL',
          slug: 'minimal',
          description: 'Clean lines, pure forms, and essential beauty. Our MINIMAL collection embraces the philosophy that less is more, featuring furniture that speaks through simplicity and perfect proportions.',
          image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuB3tRaV6p1Z0RzKC7IvkjZQtghnRAxSMvOEC1CTZAt_m6y89IWKPpfTNr7QnOO4bDybNl7PFSFsgEpjEy6lwo__Dpm8H_5J21KP7GaGTpQI2Vhi_DFqBATzarKBo_j_IbpvnpllMP8YutG8pQyUZr8lLySX0-Wp0Cj-9iM7j2dTPNt4kCfodmJXP1Z9ZhcabPYjloWJk8nt2Uc1YhEkLuv7rFwBfaYDCMFzasl-cpKCCL9cFEKLXaZE6Av8E4QZ1Us_Jnrqkn_5Q8',
          features: ['Sleek Design', 'Functional', 'Space-Saving', 'Modern Aesthetic'],
          products: [
            { name: 'Minimal Desk', price: '₹1,200' },
            { name: 'Simple Chair', price: '₹450' },
            { name: 'Clean Shelf', price: '₹800' }
          ]
        },
        {
          id: 2,
          name: 'OAKEN',
          slug: 'oaken',
          description: 'Timeless oak craftsmanship meets contemporary design. OAKEN celebrates the natural beauty of wood with pieces that showcase grain patterns, warm tones, and enduring quality.',
          image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuDz3VdO4yVa4uQX4s3oRCM1pSbYGeHFoTG_cOMerfbTd6ZIIr93ZMJctLXDhjXoZKFKUYHz_JBYTDEBCxQxCn6RP0cpgo_OgxLsJFwDq28pJhrZgCGU3WmAnvtaYHBTrS6cEunX_jiJSxxZnXE3gcJqekkZmOkZhGypqYhzrp5JhEzxKF3KMje-ZERAwRVVhBwT_lmZ2-i8fdzsMHSZ8iD7JYgAQlEVWQe-4m4gHX6VpBkSAClT0EVl6q0iSX7JYI44QB3sKXNGtYrC',
          features: ['Solid Oak', 'Natural Finish', 'Durable', 'Classic Style'],
          products: [
            { name: 'Oak Dining Table', price: '₹2,800' },
            { name: 'Oak Bookshelf', price: '₹1,500' },
            { name: 'Oak Coffee Table', price: '₹1,100' }
          ]
        },
        {
          id: 3,
          name: 'MODERNIST',
          slug: 'modernist',
          description: 'Bold forms, innovative materials, and avant-garde design. MODERNIST pushes boundaries with pieces that challenge traditional furniture concepts while maintaining functionality.',
          image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuBSQIWC_9b-9Gjt-ZNGBs587OPT_qMy5VqudzWVqUI_4yXpJr_vMqfHqsPBLjDqRB-N5vAfFk-vxuRgE1UGgUD-JTZHiO5u0vW55uTnG9JIumYKaJFR3sQNBWFKaKsycDW9l91c0glxzkJiUPIGibUIdVl1FO6wO1xiRHd96W4Oog1Uf86cUsvu5T0DtoVDNCY1JTTWXyjIhKcpF6HcbjOvARl6r8ZptiFfG4yplfajJQCgsOuP9GRQxa0_KY8HAdjNQHIdbTaFRVF5',
          features: ['Innovative Design', 'Mixed Materials', 'Artistic', 'Statement Pieces'],
          products: [
            { name: 'Geometric Sofa', price: '₹3,200' },
            { name: 'Abstract Table', price: '₹1,800' },
            { name: 'Sculptural Chair', price: '₹950' }
          ]
        },
        {
          id: 4,
          name: 'LUMEN',
          slug: 'lumen',
          description: 'Illuminated elegance and radiant design. LUMEN integrates light and furniture, creating pieces that glow with warmth and transform spaces through luminous beauty.',
          image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuC2Gbn0IJg0rCrTjCIYMabD86Rxx8kB1eI0CSXrvRoI1w-e4-M5XEK7pQD1GCXRrP1Ovtm5hRk83jPXJsKm8-PUGyX4T5tDNIU96RhcI4ews7ER3_UAj0c440ux1T2aPEHr_5ln_lPU_y1XOUNRy3bgRczZ0GMN9IeC1uC8PB7QGmdffWsTBTP_c4pQClLnczyA0mHGGcTPlfKy0C-DNRfzW41DvkkVlFNIK2SwF3ABDopXKMU9dAIThZCDd4XdhSjEj96pHJkZY_bA',
          features: ['LED Integration', 'Ambient Lighting', 'Smart Features', 'Modern Tech'],
          products: [
            { name: 'Glowing Console', price: '₹2,400' },
            { name: 'Light Panel', price: '₹1,300' },
            { name: 'Illuminated Shelf', price: '₹1,700' }
          ]
        },
        {
          id: 5,
          name: 'ARBOR',
          slug: 'arbor',
          description: 'Nature-inspired design with sustainable materials. ARBOR brings the outdoors inside with organic shapes, natural textures, and eco-friendly craftsmanship.',
          image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuAOjNQ5-AjF4G4D6WxhZ03g0OSPhqASmr05dUY0j3NtN7zjr5mz1B0t8I8GVtWpkx2ULcAxqTZnVYe_pMCKVL0UPQBknMjr3BoeJkGzecYWB_d2HVdpmFyCaBlYbDrj9PeUXqGngcwc8Zx20h_LtBxlWzEjjHmS5JCIDf4ENqQkh4cAb8rU35wvh09ZJxk_uc6i9v--902aoUr6EFmlQX1wZ__4GM15BJvCtW2NbNrGTq1l55k1XSy06eZ7CwJwjhpLNfO8ft-ThN88',
          features: ['Sustainable', 'Natural Materials', 'Eco-Friendly', 'Organic Design'],
          products: [
            { name: 'Branch Table', price: '₹1,900' },
            { name: 'Leaf Chair', price: '₹650' },
            { name: 'Root Console', price: '₹2,100' }
          ]
        }
      ];
      setBrands(defaultBrands);
      localStorage.setItem('brands', JSON.stringify(defaultBrands));
    }
  }, []);

  // Save brands to localStorage whenever they change
  useEffect(() => {
    if (brands.length > 0) {
      localStorage.setItem('brands', JSON.stringify(brands));
    }
  }, [brands]);

  const showToastNotification = (message, type) => {
    setShowToast({ show: true, message, type });
    setTimeout(() => {
      setShowToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const addBrand = (newBrand) => {
    const brandWithId = {
      ...newBrand,
      id: Date.now(),
      slug: newBrand.name.toLowerCase().replace(/\s+/g, '-')
    };
    setBrands(prev => [...prev, brandWithId]);
    showToastNotification('Brand added successfully!', 'success');
  };

  const updateBrand = (updatedBrand) => {
    setBrands(prev => 
      prev.map(brand => 
        brand.id === updatedBrand.id ? updatedBrand : brand
      )
    );
    showToastNotification('Brand updated successfully!', 'success');
  };

  const deleteBrand = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    setBrands(prev => prev.filter(brand => brand.id !== brandId));
    showToastNotification(`"${brand.name}" deleted successfully!`, 'error');
  };

  const getBrandBySlug = (slug) => {
    return brands.find(brand => brand.slug === slug);
  };

  const value = {
    brands,
    addBrand,
    updateBrand,
    deleteBrand,
    getBrandBySlug,
    showToast
  };

  return (
    <BrandContext.Provider value={value}>
      {children}
      {showToast.show && (
        <div
          className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300 ${
            showToast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white`}
        >
          {showToast.message}
        </div>
      )}
    </BrandContext.Provider>
  );
};
